import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppState } from 'react-native';
import { useIsFocused, useNavigation } from '@react-navigation/native';
import { formatMpQrPendingBannerLabel, sumPendingMpQrAmount } from '../../../mercadopago/application/posMpQrPendingPresentation'
import { listPendingMpQrSalesForBranch } from '../../../mercadopago/infra/pendingMpQrSalesRepo'
import type { PendingMpQrSaleSummary } from '../../../mercadopago/infra/pendingMpQrSalesRepo';
import { openSaleDetail } from '../../../sales/public-api';
import { captureCatch } from '../../../shared/sentry/sentryUser';
import { usePosCheckoutStore } from '../../store/usePosCheckoutStore';

const POLL_MS = 30_000;

type Params = {
  branchId: string | null | undefined;
  mercadopagoQrMethodAllowed: boolean;
  openSalesHistory?: () => void;
};

export function usePosOperationalAlerts({
  branchId,
  mercadopagoQrMethodAllowed,
  openSalesHistory,
}: Params) {
  const navigation = useNavigation<any>();
  const isFocused = useIsFocused();
  const showQrModal = usePosCheckoutStore((s) => s.showQrModal);
  const lastMpQrSaleId = usePosCheckoutStore((s) => s.lastMpQrSaleId);
  const setShowQrModal = usePosCheckoutStore((s) => s.setShowQrModal);
  const setLastMpQrSaleId = usePosCheckoutStore((s) => s.setLastMpQrSaleId);
  const setLastMpQrAmount = usePosCheckoutStore((s) => s.setLastMpQrAmount);
  const setLastMpQrFiscalMode = usePosCheckoutStore((s) => s.setLastMpQrFiscalMode);

  const [pendingMpQrSales, setPendingMpQrSales] = useState<PendingMpQrSaleSummary[]>([]);

  const refresh = useCallback(async () => {
    if (!branchId) {
      setPendingMpQrSales([]);
      return;
    }

    const pendingSales = await (mercadopagoQrMethodAllowed
      ? listPendingMpQrSalesForBranch(branchId)
      : Promise.resolve([]));

    setPendingMpQrSales(pendingSales);
  }, [branchId, mercadopagoQrMethodAllowed]);

  // El tab de POS queda montado aunque no esté enfocado: solo refrescar/pollear
  // cuando la pantalla es visible, si no el intervalo corre durante toda la sesión.
  useEffect(() => {
    if (!isFocused) return;
    void refresh().catch(captureCatch('pos', 'operational-alerts-refresh'));
  }, [refresh, isFocused, showQrModal, lastMpQrSaleId]);

  useEffect(() => {
    if (!isFocused) return;
    const timer = setInterval(() => {
      void refresh().catch(captureCatch('pos', 'operational-alerts-poll'));
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [refresh, isFocused]);

  useEffect(() => {
    if (!isFocused) return;
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') void refresh().catch(captureCatch('pos', 'operational-alerts-active'));
    });
    return () => sub.remove();
  }, [refresh, isFocused]);

  const visiblePendingMpQrSales = useMemo(
    () =>
      pendingMpQrSales.filter(
        (sale) => !(showQrModal && lastMpQrSaleId != null && sale.saleId === lastMpQrSaleId)
      ),
    [lastMpQrSaleId, pendingMpQrSales, showQrModal]
  );

  const mpQrBanner = useMemo(() => {
    if (visiblePendingMpQrSales.length === 0) return null;
    const totalAmount = sumPendingMpQrAmount(visiblePendingMpQrSales);
    return {
      label: formatMpQrPendingBannerLabel(visiblePendingMpQrSales.length, totalAmount),
      primarySale: visiblePendingMpQrSales[0] ?? null,
    };
  }, [visiblePendingMpQrSales]);

  const resumeMpQr = useCallback(
    (sale: PendingMpQrSaleSummary) => {
      setLastMpQrSaleId(sale.saleId);
      setLastMpQrAmount(sale.pendingAmount > 0 ? sale.pendingAmount : sale.total);
      setLastMpQrFiscalMode(sale.fiscalMode);
      setShowQrModal(true);
    },
    [setLastMpQrAmount, setLastMpQrFiscalMode, setLastMpQrSaleId, setShowQrModal]
  );

  const handleResumeMpQr = useCallback(() => {
    const sale = mpQrBanner?.primarySale;
    if (!sale) return;
    resumeMpQr(sale);
  }, [mpQrBanner?.primarySale, resumeMpQr]);

  const handleOpenMpQrInVentas = useCallback(() => {
    const sale = mpQrBanner?.primarySale;
    if (!sale) {
      openSalesHistory?.();
      return;
    }
    openSaleDetail(navigation, { saleId: sale.saleId });
  }, [mpQrBanner?.primarySale, navigation, openSalesHistory]);

  return {
    mpQrBanner,
    handleResumeMpQr,
    handleOpenMpQrInVentas,
    refreshOperationalAlerts: refresh,
  };
}
